/**
 * @file src/views/CreateInviteView/state.ts
 * @exports clearLegacyInviteStorage, InviteFormStore, useInviteFormStore, inviteDraftAtom, deletedInviteApplicantsAtom, selectedPackageIdAtom, selectedPassTypeIdAtom, invitePlatformTypeAtom, onSubmitReturnUrlAtom, invitePostbackWebhookUrlAtom, inviteMetadataAtom, inviteApplicantsAtom
 * @summary Page-local invite form state defaults to Mobile and is discarded when the composer unmounts.
 */
import { atom, type createStore } from "jotai";
import { atomWithReset, RESET } from "jotai/utils";
import { createContext, useContext } from "react";
import type { ApplicantData } from "./types";

export const InviteFormStore = createContext<ReturnType<typeof createStore> | null>(null);
/** Gets the composer-owned store without shadowing application-wide atoms. */
export function useInviteFormStore() {
	const store = useContext(InviteFormStore);
	if (!store) throw new Error("Invite form must be mounted inside its composer");
	return store;
}

const emptyInviteDraft = {
	packageId: "",
	passTypeId: undefined as string | undefined,
	platform: "Mobile",
	returnUrl: null as string | null,
	postbackUrl: "",
	metadata: "",
	applicants: [] as ApplicantData[],
};
export const inviteDraftAtom = atomWithReset(emptyInviteDraft);
export const deletedInviteApplicantsAtom = atom<Record<string, ApplicantData>>({});

/** Field lenses share one temporary form value. */
function inviteField<Key extends keyof typeof emptyInviteDraft>(key: Key) {
	type Value = (typeof emptyInviteDraft)[Key];
	return atom(
		(get) => get(inviteDraftAtom)[key],
		(get, set, update: Value | typeof RESET | ((value: Value) => Value)) => {
			const draft = get(inviteDraftAtom);
			const value =
				update === RESET ? emptyInviteDraft[key] : typeof update === "function" ? update(draft[key]) : update;
			set(inviteDraftAtom, { ...draft, [key]: value });
		},
	);
}
export const selectedPackageIdAtom = inviteField("packageId");
export const selectedPassTypeIdAtom = inviteField("passTypeId");
export const invitePlatformTypeAtom = inviteField("platform");
export const onSubmitReturnUrlAtom = inviteField("returnUrl");
export const invitePostbackWebhookUrlAtom = inviteField("postbackUrl");
export const inviteMetadataAtom = inviteField("metadata");
export const inviteApplicantsAtom = inviteField("applicants");

/** Removes obsolete persistent drafts; new forms never write browser storage. */
export function clearLegacyInviteStorage() {
	for (const key of [
		"invite",
		"inviteDraft",
		"invitePackage",
		"passType",
		"invitePlatformType",
		"onSubmitReturnUrl",
		"invitePostbackWebhookUrl",
		"inviteMetadata",
		"inviteApplicants",
	])
		localStorage.removeItem(key);
}
